import { Check, FileText, Info } from 'lucide-react';
import type { Upload } from '@tabservice/tabfile-client';
import { Card } from '@/components/ui/card';
import { CopyValue } from '@/components/ui/copy';
import { formatBytes, formatDateTime } from '@/lib/format';
import { cn } from '@/lib/cn';

/** One step of whatever flow is drawing the rail. */
export interface RailStage {
  id: string;
  label: string;
}

/** What the person is doing in the new e-file wizard, in the order they do it. */
export type Stage = 'format' | 'validate' | 'upload' | 'submit';

export const wizardStages: RailStage[] = [
  { id: 'format', label: 'File information' },
  { id: 'validate', label: 'Validate' },
  { id: 'upload', label: 'Upload' },
  { id: 'submit', label: 'Submit' },
];

/**
 * A correction and a replacement are the same two steps.
 *
 * Fewer steps, the same rail. Two flows that both send records to the IRS drawing their
 * progress two different ways is the kind of difference somebody notices without being able
 * to say what is wrong, and then trusts the screen slightly less.
 */
export const amendStages: RailStage[] = [
  { id: 'records', label: 'Records' },
  { id: 'checks', label: 'Validate' },
  { id: 'submit', label: 'Submit' },
];

/** When each stage was finished, kept so a finished stage can say when rather than only that. */
export type StageTimes = Partial<Record<string, string>>;

/**
 * The wizard's right-hand rail: what is being filed, and how far along it is.
 *
 * The same shape the Tab Service portal uses for its upload, and for the same reason. A row
 * of numbered circles across the top says which step you are on and nothing else; read down
 * the side, with the time each finished step finished, it also says what you have already
 * done - which is the question somebody coming back to a draft an hour later is asking.
 */
export function WizardRail({
  current,
  times,
  stages = wizardStages,
  formType,
  formatLabel,
  subject,
  upload,
  sizeBytes,
  fileName,
}: {
  current: string | 'done';
  times: StageTimes;
  /** The steps this flow has. Four for a new e-file, three for an amendment. */
  stages?: RailStage[];
  /** Null until a row file's form is chosen, or until a self-describing file has been read. */
  formType: string | null;
  formatLabel: string | null;
  /** What the top card calls what is being filed. "Form type" unless a flow says otherwise. */
  subject?: string;
  upload: Upload | null;
  sizeBytes: number | null;
  /** The file picked on this machine, known before the server has been told about it. */
  fileName: string | null;
}) {
  const currentIndex = stages.findIndex((stage) => stage.id === current);

  return (
    <div className="flex flex-col gap-6" data-tour="wizard-steps">
      {/*
        What is being filed, at the top, where the portal puts it. Before a form is known it
        names the format instead, which is the most that is true at that point.
      */}
      <Card className="p-5">
        <div
          className={cn(
            'flex items-center gap-3 rounded-lg border border-[color:var(--ts-border-soft)]',
            'bg-[color:var(--ts-blue-bglayout)] p-3',
          )}
        >
          <span
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
              'border border-[color:var(--ts-border-soft)] bg-[color:var(--ts-surface)]',
            )}
          >
            <FileText className="h-5 w-5 text-primary" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
              {subject ?? (formType ? 'Form type' : 'Format')}
            </p>
            <p className="mt-0.5 truncate text-lg font-semibold text-primary">
              {formType ?? formatLabel ?? 'Not chosen yet'}
            </p>
          </div>
        </div>

        <h2 className="mt-6 text-lg font-semibold text-primary">Progress</h2>
        <ol aria-label="Progress" className="mt-5 flex flex-col">
          {stages.map((stage, index) => {
            const done = current === 'done' || currentIndex > index;
            const active = currentIndex === index && current !== 'done';
            const at = times[stage.id];
            const last = index === stages.length - 1;
            return (
              <li key={stage.id} className="flex gap-3">
                {/* The mark and the line under it, so the steps read as one column. */}
                <div className="flex flex-col items-center">
                  <span
                    aria-hidden="true"
                    className={cn(
                      'flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
                      'transition-[background-color,box-shadow] duration-200 ease-out',
                      done && 'bg-primary text-primary-foreground',
                      active && 'bg-surface shadow-[inset_0_0_0_4px_var(--ts-primary)]',
                      !done && !active && 'bg-surface shadow-[inset_0_0_0_2px_var(--ts-border)]',
                    )}
                  >
                    {done ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : null}
                  </span>
                  {!last ? (
                    <span
                      aria-hidden="true"
                      className={cn(
                        'my-1.5 w-0.5 flex-1 rounded-full transition-colors duration-200 ease-out',
                        done ? 'bg-[color:var(--ts-primary-light)]' : 'bg-[color:var(--ts-border)]',
                      )}
                    />
                  ) : null}
                </div>
                <div className={cn('min-w-0 pt-0.5 pb-6', last && 'pb-0')}>
                  <p
                    className={cn(
                      'text-base',
                      active ? 'font-semibold text-foreground' : done ? 'text-gray-3' : 'text-gray-1',
                    )}
                    aria-current={active ? 'step' : undefined}
                  >
                    {stage.label}
                  </p>
                  {done && at ? (
                    <p className="mt-1 text-sm text-muted-foreground">{formatDateTime(at)}</p>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      </Card>

      {upload ? (
        <DraftCard upload={upload} sizeBytes={sizeBytes} formTypeLabel={formType} fileName={fileName} />
      ) : null}
    </div>
  );
}

/**
 * The draft's own facts, one to a line.
 *
 * They used to run across the page as a single pipe-separated strip, which is readable when
 * there are three of them and a wall when there are six.
 */
function DraftCard({
  upload,
  sizeBytes,
  formTypeLabel,
  fileName,
}: {
  upload: Upload;
  sizeBytes: number | null;
  formTypeLabel: string | null;
  fileName: string | null;
}) {
  // Neither the format nor the start time is repeated here. The card at the top of the rail
  // names the format when no form is known yet, and the first step of Progress is stamped
  // with the moment this draft was started - the same fact, said once.
  const rows: { label: string; value: React.ReactNode }[] = [
    // The draft is created before the file is chosen and told about it afterwards, so the
    // server's copy of the name lags the one on screen. Whichever exists is the answer; the
    // one on this machine is the more recent of the two.
    { label: 'File', value: upload.fileName || fileName || 'Not chosen yet' },
  ];
  if (sizeBytes) rows.push({ label: 'Size', value: formatBytes(sizeBytes) });
  if (upload.formType ?? formTypeLabel) rows.push({ label: 'Form', value: upload.formType ?? formTypeLabel });
  if (upload.taxYear) rows.push({ label: 'Tax year', value: upload.taxYear });

  return (
    <Card className="p-5">
      <h2 className="text-base font-medium text-primary">Draft</h2>
      <div className="mt-3">
        <CopyValue value={upload.id} label="draft id" />
      </div>
      <dl className="mt-4 flex flex-col gap-3">
        {rows.map((row) => (
          <div key={row.label} className="flex items-baseline justify-between gap-3">
            <dt className="text-sm text-muted-foreground">{row.label}</dt>
            <dd className="min-w-0 truncate text-right text-base text-gray-3">{row.value}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

/**
 * Nothing has been filed yet, said plainly and kept on screen.
 *
 * The step somebody is on says where they are; it does not say that leaving now files
 * nothing and loses nothing. The portal keeps that sentence up through its whole upload, and
 * the question it answers - "have I already sent this to the IRS?" - is a worse one to get
 * wrong here than there.
 */
export function NotFiledYet({ hasDraft }: { hasDraft: boolean }) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-lg px-4 py-3 shadow-[var(--ts-shadow-card)]',
        // The page behind this is already a pale blue wash, and the tray fill it used to
        // carry was near enough the same colour to read as nothing at all. The info pill's
        // own fill and border are what the rest of the app uses when something must be seen.
        'border border-[color:var(--ts-pill-info-border)] bg-[color:var(--ts-pill-info-bg)]',
      )}
    >
      <Info className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
      <div className="min-w-0 text-base">
        <p className="font-semibold text-primary">Nothing has been sent to the IRS yet</p>
        <p className="mt-0.5 text-gray-2">
          {hasDraft
            ? 'This is a draft. It waits under Drafts until you submit it, and you can close this page and come back to it.'
            : 'Your file is checked here first. Nothing reaches the IRS until you confirm it on the last step.'}
        </p>
      </div>
    </div>
  );
}
